import { Icon } from "@iconify/react/dist/iconify.js";
import { Button, message } from "antd";
import clsx from "clsx";
import { Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import usePostData from "../../api/usePostData";
import AppInput from "../../common/AppInput";
import Recaptcha from "../../components/Recaptcha/Recaptcha";
import { saveUserData } from "../../redux/slices/authSlice";
import { RegisterDone } from "../../utils/mixpanel";
import { registerPasswordValidation } from "../../utils/validations";

/**
 * @typedef {Object} UserInfo
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} dob
 * @property {string} nationality
 * @property {string} gender
 */

/**
 * A component that represents the password step form of the registeration process.
 * It gets shown only when the user that is creating the new account a guest, not an agent.
 * It is stored in a separate component because there are two different places where the registeration takes place with some differences in each.
 * It is intended for this component to not call the useContext hook directly, but for its parent to do so instead.
 *
 * @param {Object} props
 * @param {string} props.email - The submitted value of the email
 * @param {string} props.phone - The submitted value of the phone
 * @param {UserInfo} props.userInfo - The submitted values of the user info
 * @param {()=>void} props.onPreviousClick - A function to be called when the user clicks the previous button
 * @returns {JSX.Element}
 */

const PasswordsStepForm = ({
  initialValues,
  email,
  phone,
  userInfo,
  onPreviousClick,
}) => {
  const { t } = useTranslation("global");
  const navigate = useNavigate();
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const dispatch = useDispatch();
  const recaptchaRef = useRef(null);
  const { postData, errors } = usePostData({});
  const onSubmit = async (values) => {
    if (!recaptchaToken) {
      return message.error(t("recaptchaRequired"));
    }
    const res = await postData({
      route: "api/method/airport_transport.api.user.register",
      data: {
        email,
        phone,
        first_name: userInfo.firstName,
        last_name: userInfo.lastName,
        dob: userInfo.dob,
        nationality: userInfo.nationality,
        gender: userInfo.gender,
        password: values.password,
      },
      params: {
        headers: {
          recaptchaToken,
        },
      },
    });
    if (res.data?.data) {
      const { token, ...userData } = res.data.data;
      dispatch(
        saveUserData({
          token,
          userData: {
            username: userData.name,
            email: userData.email,
          },
        })
      );
      RegisterDone(
        `${userInfo.firstName} ${userInfo.lastName}`,
        userData.email,
        phone
      );
      navigate("/", { state: {}, replace: true });
    }
    recaptchaRef?.current.reset();
    setRecaptchaToken(null);
  };
  useEffect(() => {
    if (errors) {
      const errorMsg = errors?.error || "";
      if (errorMsg.toLowerCase() === "mobile number must be unique") {
        message.error(t("errors.mobile_number_must_be_unique"));
      } else {
        message.error(`${errorMsg}`);
      }
    }
  }, [errors]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={registerPasswordValidation(t)}
      onSubmit={onSubmit}
      validate={(values) => {
        // Return all errors at once
        return registerPasswordValidation(t)
          .validate(values, { abortEarly: false })
          .then(() => {})
          .catch((err) => {
            return err.inner.reduce((obj, e) => {
              if (!(e.path in obj)) obj[e.path] = [];
              obj[e.path] = obj[e.path].concat(e.errors);
              return obj;
            }, {});
          });
      }}
    >
      {({ errors, touched, values }) => (
        <Form className="mx-auto w-full">
          <AppInput
            name="password"
            type="password"
            label={t("my_profile.password_text")}
            placeholder={t("my_profile.password_text")}
            hideError
          />
          <AppInput
            name="confirmPassword"
            type="password"
            label={t("my_profile.confirm_password")}
            placeholder={t("my_profile.confirm_password")}
            error={errors.confirmPassword}
            touched={touched.confirmPassword}
          />{" "}
          <div className="my-3">
            <div
              className={clsx("my-2 flex flex-row justify-start items-center", {
                "text-text_steel_blue": values.password,
                "text-text_warning":
                  errors["password"] &&
                  (errors["password"].includes(
                    t("errors.password_min_error")
                  ) ||
                    errors["password"].includes(
                      t("errors.password_max_error")
                    )),
              })}
            >
              <Icon icon="teenyicons:tick-outline" width="24px" height="24px" />
              <p className="text-sm ms-2">
                {t("my_profile.password_8_20_text")}
              </p>
            </div>

            <div
              className={clsx("my-2 flex flex-row justify-start items-center", {
                "text-text_steel_blue": values.password,
                "text-text_warning": errors["password"]?.includes(
                  t("errors.password_uppercase_error")
                ),
              })}
            >
              <Icon icon="teenyicons:tick-outline" width="24px" height="24px" />
              <p className="text-sm ms-2">
                {t("my_profile.password_uppercase_text")}
              </p>
            </div>

            <div
              className={clsx("my-2 flex flex-row justify-start items-center", {
                "text-text_steel_blue": values.password,
                "text-text_warning": errors["password"]?.includes(
                  t("errors.password_lowercase_error")
                ),
              })}
            >
              <Icon icon="teenyicons:tick-outline" width="24px" height="24px" />
              <p className="text-sm ms-2">
                {t("my_profile.password_lowercase_text")}
              </p>
            </div>

            <div
              className={clsx("my-2 flex flex-row justify-start items-center", {
                "text-text_steel_blue": values.password,
                "text-text_warning": errors["password"]?.includes(
                  t("errors.password_symbol_error")
                ),
              })}
            >
              <Icon icon="teenyicons:tick-outline" width="24px" height="24px" />
              <p className="text-sm ms-2">
                {t("my_profile.password_symbol_text")}
              </p>
            </div>
          </div>
          <div>
            <Recaptcha
              recaptchaRef={recaptchaRef}
              onChange={(value) => {
                setRecaptchaToken(value);
              }}
            />
          </div>
          <div className="w-full mt-3 flex flex-col  md:flex-row justify-between items-center">
            <Button
              className="bg-bg_btn_back w-1/2 text-text_white hover:bg-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2"
              onClick={onPreviousClick}
              type="button"
              htmlType="button"
            >
              {t("previous_text")}
            </Button>
            <Button
              className="bg-background_steel_blue w-1/2 text-text_white hover:bg-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2"
              label={t("submit_text")}
              type="submit"
              htmlType="submit"
            >
              {t("submit_text")}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default PasswordsStepForm;
